/**
 * Truck Fitment Selector (v3.2)
 * --------------------------------------------------------------------------
 * Two operating modes set per-product via block settings:
 *
 *   variant_mapping (default)
 *     Used by Gear Vault W, Molle Panel W, SD Molle Panel W.
 *     Truck selection drives variant selection. Native variant picker hidden.
 *
 *   metadata_only
 *     Used by Load Bars W.
 *     Customer chooses Load Bar variant themselves. Truck selection adds
 *     line item properties only. Native variant picker stays visible.
 *
 * Camper Model compatibility (3-state per model):
 *   compatible  — customer can buy
 *   show_block  — visible in dropdown, picking it disables Add to Cart and
 *                 prompts customer to contact for alternatives
 *   hide        — not shown in dropdown at all
 *
 * Add-to-Cart button text:
 *   In variant_mapping mode, the button text is overridden by the block while
 *   the customer is making selections. Once a real variant resolves, control
 *   is released so Shopify's native inventory state (Add to Cart / Sold Out)
 *   shows correctly. A MutationObserver re-applies our text if the theme
 *   rewrites the button after we set it (which Shopify's variant change
 *   handlers do).
 */

class TruckFitmentSelector extends HTMLElement {
  constructor() {
    super();
    this.STORAGE_KEY = 'lpo_truck_fitment_v3';

    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.productFormId = this.dataset.productFormId;
    this.fitmentUrl = this.dataset.fitmentUrl;
    this.contactUrl = this.dataset.contactUrl || '/pages/contact';

    this.mode = this.dataset.mode || 'variant_mapping';
    this.axis = this.dataset.axis || 'height';
    this.metadataVariantPrompt = this.dataset.metadataVariantPrompt || 'Choose your option to continue';

    this.variantByLetter = {
      A: this.dataset.variantA || '',
      B: this.dataset.variantB || '',
      C: this.dataset.variantC || '',
      D: this.dataset.variantD || '',
    };
    this.fallbackVariantId = this.dataset.fallbackVariant || '';

    // Parse "V1:compatible|V2:show_block" into { V1: 'compatible', V2: 'show_block' }
    this.camperStates = {};
    (this.dataset.camperModels || '').split('|')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const [model, state] = entry.split(':').map((s) => s.trim());
        if (model && state) this.camperStates[model] = state;
      });

    this.visibleCamperModels = Object.keys(this.camperStates);
    this.buyableCamperModels = Object.keys(this.camperStates)
      .filter((m) => this.camperStates[m] === 'compatible');

    this.makeSelect = this.querySelector('[data-fitment-field="make"]');
    this.modelSelect = this.querySelector('[data-fitment-field="model"]');
    this.tallSelect = this.querySelector('[data-fitment-field="tall"]');
    this.camperSelect = this.querySelector('[data-fitment-field="camper"]');

    this.tallRow = this.querySelector('[data-fitment-row="tall"]');
    this.camperRow = this.querySelector('[data-fitment-row="camper"]');

    this.notListedBtn = this.querySelector('[data-fitment-not-listed]');

    // New structured summary card (replaces simple text status)
    this.statusEl = this.querySelector('[data-fitment-status]');
    this.summaryIcon = this.querySelector('[data-fitment-summary-icon]');
    this.summaryTitle = this.querySelector('[data-fitment-summary-title]');
    this.summaryDetail = this.querySelector('[data-fitment-summary-detail]');

    // Progress track — JS fills dots based on visible-required-fields count
    this.progressEl = this.querySelector('[data-fitment-progress]');

    this.props = {
      truck: this.querySelector('[data-fitment-property="truck"]'),
      tall: this.querySelector('[data-fitment-property="tall"]'),
      camper: this.querySelector('[data-fitment-property="camper"]'),
      height: this.querySelector('[data-fitment-property="height"]'),
      width: this.querySelector('[data-fitment-property="width"]'),
    };

    this.fitment = null;
    this.notListedActive = false;

    // Placeholder button state (variant_mapping mode only).
    // While selection is in progress, we hide Shopify's buy-buttons
    // CONTAINER and show our placeholder in its place. When selection
    // completes, we hide the placeholder and show the container; Shopify's
    // own logic determines which button within the container is visible
    // (Add to Cart vs. Sold Out / back-in-stock alert).
    this._placeholderBtn = null;
    this._buyButtonsContainer = null;
    this._placeholderActive = false;
  }

  async connectedCallback() {
    this.populateCamperOptions();

    // Placeholder runs in BOTH modes:
    //   variant_mapping: hides Shopify's buttons until truck selection resolves
    //   metadata_only:   hides them until truck AND native variant are picked
    this.setupPlaceholderButton();

    if (this.mode === 'variant_mapping') {
      this.hideNativeVariantPicker();
    } else {
      this.watchNativeVariantPicker();
    }

    this.disableAddToCart('Select your truck to continue');

    try {
      await this.loadFitment();
    } catch (err) {
      console.error('[TruckFitment] Failed to load fitment data:', err);
      this.setStatus('Unable to load fitment data. Please refresh the page.', 'error');
      return;
    }

    this.populateMakes();
    this.attachEvents();
    this.updateProgress();
    this.restoreFromStorage();
  }

  disconnectedCallback() {
    // Clean up: remove placeholder, restore any hidden buy buttons
    this.deactivatePlaceholder();
    if (this._placeholderBtn) {
      this._placeholderBtn.remove();
      this._placeholderBtn = null;
    }
  }

  // ---------------- Camper dropdown population ----------------

  /**
   * Populate the Camper Model dropdown from this.camperStates. The dropdown
   * row is hidden when 0 or 1 models are visible (no dropdown needed); shown
   * when 2+ models are visible (customer must pick).
   * "Visible" means state is compatible OR show_block (not "hide").
   */
  populateCamperOptions() {
    if (!this.camperSelect) return;

    this.camperSelect.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = 'Select camper';
    this.camperSelect.appendChild(ph);

    for (const model of this.visibleCamperModels) {
      const opt = document.createElement('option');
      opt.value = model;
      opt.textContent = model;
      // Note: we don't disable the show_block option — the customer needs
      // to be able to pick it so they get a contact-us message.
      this.camperSelect.appendChild(opt);
    }

    // Show/hide the row based on count
    if (this.camperRow) {
      this.camperRow.hidden = this.visibleCamperModels.length < 2;
    }
  }

  // ---------------- Fitment data ----------------

  async loadFitment() {
    const res = await fetch(this.fitmentUrl);
    if (!res.ok) throw new Error(`Fitment fetch failed: ${res.status}`);
    const data = await res.json();
    this.fitment = (data.trucks || []).map((row) => ({
      make: String(row.make || '').trim(),
      model: String(row.model || '').trim(),
      height_base: String(row.height_base || '').trim().toUpperCase(),
      height_tall: row.height_tall ? String(row.height_tall).trim().toUpperCase() : '',
      width_base: row.width_base ? String(row.width_base).trim().toUpperCase() : '',
    })).filter((r) => r.make && r.model && r.height_base);
  }

  findTruck(make, model) {
    return this.fitment.find((r) => r.make === make && r.model === model) || null;
  }

  // ---------------- Make / Model dropdowns ----------------

  populateMakes() {
    const makes = [...new Set(this.fitment.map((r) => r.make))].sort();
    this.fillSelect(this.makeSelect, makes, 'Select make');
  }

  populateModels(make) {
    const models = [...new Set(
      this.fitment.filter((r) => r.make === make).map((r) => r.model)
    )].sort();
    this.fillSelect(this.modelSelect, models, 'Select model');
    this.modelSelect.disabled = models.length === 0;
  }

  fillSelect(select, values, placeholder) {
    select.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholder;
    select.appendChild(ph);
    for (const v of values) {
      const opt = document.createElement('option');
      opt.value = String(v);
      opt.textContent = String(v);
      select.appendChild(opt);
    }
    select.value = '';
  }

  // ---------------- Row visibility ----------------

  showRow(row) { if (row) row.hidden = false; }

  hideRow(row) {
    if (!row) return;
    row.hidden = true;
    const sel = row.querySelector('select');
    if (sel) sel.value = '';
  }

  updateTallRowVisibility(truck) {
    if (truck && truck.height_tall) this.showRow(this.tallRow);
    else this.hideRow(this.tallRow);
  }

  // ---------------- Events ----------------

  attachEvents() {
    this.makeSelect.addEventListener('change', () => this.recompute());
    this.modelSelect.addEventListener('change', () => this.recompute());
    if (this.tallSelect) this.tallSelect.addEventListener('change', () => this.recompute());
    if (this.camperSelect) this.camperSelect.addEventListener('change', () => this.recompute());
    this.notListedBtn.addEventListener('click', () => this.onNotListed());
  }

  watchNativeVariantPicker() {
    const form = document.getElementById(this.productFormId);
    if (!form) return;
    const input = form.querySelector('input[name="id"]');
    if (!input) return;
    const handler = () => this.recompute();
    input.addEventListener('change', handler);
    input.addEventListener('input', handler);
    this._nativeVariantWatcher = { input, handler };
  }

  // ---------------- Core selection logic ----------------

  recompute() {
    if (this.notListedActive) this.notListedActive = false;
    this.updateProgress();

    const make = this.makeSelect.value;
    if (!make) {
      this.resetDownstream('make');
      this.disableAddToCart('Select your truck to continue');
      return;
    }

    const validModelsForMake = new Set(
      this.fitment.filter((r) => r.make === make).map((r) => r.model)
    );
    if (!validModelsForMake.has(this.modelSelect.value)) {
      this.populateModels(make);
    }

    const model = this.modelSelect.value;
    if (!model) {
      this.resetDownstream('model');
      this.disableAddToCart('Select your model');
      return;
    }

    const truck = this.findTruck(make, model);
    if (!truck) {
      this.disableAddToCart('No fitment found. Try "My truck isn\'t listed".');
      return;
    }

    this.updateTallRowVisibility(truck);
    this.updateProgress();

    // Tall Edition
    let tallChoice = null;
    let height = truck.height_base;
    if (truck.height_tall) {
      const tallVal = this.tallSelect.value;
      if (!tallVal) {
        this.disableAddToCart('Choose Tall Edition: Yes or No');
        return;
      }
      tallChoice = tallVal === 'yes';
      if (tallChoice) height = truck.height_tall;
    }

    // Camper Model — depends on what's visible (compatible + show_block)
    let camper = null;
    if (this.visibleCamperModels.length === 0) {
      // No camper models defined for this product — proceed without one
      camper = null;
    } else if (this.visibleCamperModels.length === 1) {
      // Only one model visible — auto-apply, but check its state
      camper = this.visibleCamperModels[0];
      if (this.camperStates[camper] === 'show_block') {
        // Edge case: single visible model is incompatible (probably a misconfig)
        this.disableAddToCart(
          'Not compatible with your camper',
          'warn',
          {
            title: `Not compatible with ${camper} campers`,
            detail: `Please <a href="${this.contactUrl}">contact us</a> for alternatives.`,
            type: 'warn',
          }
        );
        return;
      }
    } else {
      // Multiple visible — customer must pick
      camper = this.camperSelect ? this.camperSelect.value : '';
      if (!camper) {
        this.disableAddToCart('Choose your Camper Model');
        return;
      }
      // Check the picked one's state
      const state = this.camperStates[camper];
      if (state === 'show_block') {
        this.disableAddToCart(
          'Not compatible with your camper',
          'warn',
          {
            title: `Not compatible with ${camper} campers`,
            detail: `This product is ${this.buyableCamperModels.join('/')}-only. Please <a href="${this.contactUrl}">contact us</a> for alternatives.`,
            type: 'warn',
          }
        );
        // Still set the truck property so if they decide to switch camper,
        // we don't lose their truck selection — but don't enable Add to Cart.
        return;
      }
    }

    const width = truck.width_base || '';

    if (this.mode === 'variant_mapping') {
      this.applyVariantMappingSelection({
        truckLabel: `${make} ${model}`,
        tallChoice, camper, height, width,
      });
    } else {
      this.applyMetadataOnlySelection({
        truckLabel: `${make} ${model}`,
        tallChoice, camper, height, width,
      });
    }
    this.persist();
  }

  applyVariantMappingSelection({ truckLabel, tallChoice, camper, height, width }) {
    const letter = (this.axis === 'width') ? width : height;
    if (!letter) {
      this.disableAddToCart(
        `No ${this.axis} configured for this truck. Please contact us.`
      );
      return;
    }
    const variantId = this.variantByLetter[letter];
    if (!variantId) {
      this.disableAddToCart(
        `${this.axis === 'width' ? 'Width' : 'Height'} ${letter} variant not configured for this product. Please contact us.`
      );
      return;
    }

    this.setFormVariant(variantId);
    this.setProperty('truck', truckLabel);
    this.setPropertyOrClear('tall', tallChoice === null ? null : (tallChoice ? 'Yes' : 'No'));
    this.setPropertyOrClear('camper', camper);
    this.setProperty('height', height);
    this.setPropertyOrClear('width', width || null);

    this.enableAddToCart();
    const summary = this.formatSuccessStatus(truckLabel, tallChoice, camper);
    this.setStatusCard({ title: summary.title, detail: summary.detail, type: 'ok' });
  }

  applyMetadataOnlySelection({ truckLabel, tallChoice, camper, height, width }) {
    this.setProperty('truck', truckLabel);
    this.setPropertyOrClear('tall', tallChoice === null ? null : (tallChoice ? 'Yes' : 'No'));
    this.setPropertyOrClear('camper', camper);
    this.setPropertyOrClear('height', height || null);
    this.setPropertyOrClear('width', width || null);

    if (this.nativeVariantSelected()) {
      this.enableAddToCart();
      const summary = this.formatSuccessStatus(truckLabel, tallChoice, camper);
      this.setStatusCard({ title: summary.title, detail: summary.detail, type: 'ok' });
    } else {
      this.disableAddToCart(this.metadataVariantPrompt);
    }
  }

  onNotListed() {
    this.notListedActive = true;

    this.makeSelect.value = '';
    this.fillSelect(this.modelSelect, [], 'Select model');
    this.modelSelect.disabled = true;
    this.hideRow(this.tallRow);

    // Resolve camper. If we have a buyable one auto-applied, use it.
    let camper = null;
    if (this.visibleCamperModels.length === 1) {
      camper = this.visibleCamperModels[0];
      if (this.camperStates[camper] === 'show_block') {
        this.disableAddToCart(
          'Not compatible with your camper',
          'warn',
          {
            title: `Not compatible with ${camper} campers`,
            detail: `Please <a href="${this.contactUrl}">contact us</a> for alternatives.`,
            type: 'warn',
          }
        );
        return;
      }
    } else if (this.visibleCamperModels.length > 1) {
      // Truck not listed flow — camper model is optional. Customer can
      // proceed with or without picking it. If they DO pick, we still
      // check for incompatibility (show_block state) and gate accordingly.
      camper = this.camperSelect ? this.camperSelect.value : '';
      if (camper && this.camperStates[camper] === 'show_block') {
        this.disableAddToCart(
          'Not compatible with your camper',
          'warn',
          {
            title: `Not compatible with ${camper} campers`,
            detail: `This product is ${this.buyableCamperModels.join('/')}-only. Please <a href="${this.contactUrl}">contact us</a> for alternatives.`,
            type: 'warn',
          }
        );
        return;
      }
      // If camper is empty, that's fine — proceed and just don't set the
      // camper line item property. Fulfillment will follow up via "we'll
      // reach out to confirm fitment" anyway.
    }

    if (this.mode === 'variant_mapping') {
      if (!this.fallbackVariantId) {
        this.setStatus('Please contact us for fitment help.', 'error');
        this.disableAddToCart('Please contact us for fitment help.');
        return;
      }
      const fallbackLetter = Object.keys(this.variantByLetter).find(
        (k) => this.variantByLetter[k] === this.fallbackVariantId
      ) || '';

      this.setFormVariant(this.fallbackVariantId);
      this.setProperty('truck', 'Not listed');
      this.clearProperty('tall');
      this.setPropertyOrClear('camper', camper);
      if (this.axis === 'width') {
        this.setPropertyOrClear('width', fallbackLetter || null);
        this.clearProperty('height');
      } else {
        this.setPropertyOrClear('height', fallbackLetter || null);
        this.clearProperty('width');
      }

      this.enableAddToCart();
      this.setStatusCard({
        title: "Your truck isn't listed",
        detail: "Order with confidence — we'll reach out to confirm fitment before we ship.",
        type: 'warn',
      });
    } else {
      this.setProperty('truck', 'Not listed');
      this.clearProperty('tall');
      this.setPropertyOrClear('camper', camper);
      this.clearProperty('height');
      this.clearProperty('width');

      if (this.nativeVariantSelected()) this.enableAddToCart();
      else this.disableAddToCart(this.metadataVariantPrompt);
      this.setStatusCard({
        title: "Your truck isn't listed",
        detail: "Order with confidence — we'll reach out to confirm fitment before we ship.",
        type: 'warn',
      });
    }

    this.clearStorage();
  }

  // ---------------- Property helpers ----------------

  setProperty(key, value) {
    const el = this.props[key];
    if (!el) return;
    el.value = value;
    el.disabled = false;
  }

  clearProperty(key) {
    const el = this.props[key];
    if (!el) return;
    el.value = '';
    el.disabled = true;
  }

  setPropertyOrClear(key, value) {
    if (value) this.setProperty(key, value);
    else this.clearProperty(key);
  }

  // ---------------- Form & button ----------------

  getProductForm() { return document.getElementById(this.productFormId); }

  getProductFormVariantInput() {
    const form = this.getProductForm();
    return form ? form.querySelector('input[name="id"]') : null;
  }

  getAddToCartButton() {
    const form = this.getProductForm();
    return form ? form.querySelector('[name="add"], button[type="submit"]') : null;
  }

  /**
   * Get the actual text node we need to update on the button. (Kept for
   * potential future use; the placeholder approach doesn't need it.)
   */
  getAddToCartTextTarget() {
    const btn = this.getAddToCartButton();
    if (!btn) return null;
    const span = btn.querySelector('span:not(.icon):not(.loading-overlay__spinner)');
    return span || btn;
  }

  setFormVariant(variantId) {
    const input = this.getProductFormVariantInput();
    if (!input) return;
    input.value = String(variantId);
    input.disabled = false;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  nativeVariantSelected() {
    const input = this.getProductFormVariantInput();
    return !!(input && input.value && !input.disabled);
  }

  /**
   * Selection is incomplete or invalid — gate Add to Cart.
   * In both modes, show the placeholder button with the message and hide
   * Shopify's buy-buttons container.
   *
   * @param {string}  placeholderText — text shown on the placeholder button
   * @param {string}  statusType — 'info' | 'warn' | 'error'
   * @param {object|null} card — optional { title, detail, type } for the
   *                              summary card. If omitted, no card is shown
   *                              (statusType: 'info' default behavior).
   */
  disableAddToCart(placeholderText, statusType, card) {
    const btn = this.getAddToCartButton();
    if (btn) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('disabled');
    }
    if (this.mode === 'variant_mapping') {
      const variantInput = this.getProductFormVariantInput();
      if (variantInput) variantInput.disabled = true;
    }
    if (placeholderText) this.activatePlaceholder(placeholderText);
    Object.values(this.props).forEach((el) => { if (el) el.disabled = true; });

    // Card display: explicit card overrides legacy message-as-card behavior
    if (card) {
      this.setStatusCard({
        title: card.title || '',
        detail: card.detail || '',
        type: card.type || statusType || 'warn',
      });
    } else if (placeholderText && statusType && statusType !== 'info') {
      // Legacy: warn/error messages with no structured card → show as title only
      this.setStatusCard({ title: placeholderText, detail: '', type: statusType });
    } else {
      this.clearStatusCard();
    }
  }

  /**
   * Selection complete — release control. Hide the placeholder so Shopify's
   * native buy buttons take over (showing either Add to Cart or Sold Out
   * based on actual stock).
   */
  enableAddToCart() {
    const btn = this.getAddToCartButton();
    if (btn) {
      btn.removeAttribute('disabled');
      btn.classList.remove('disabled');
    }
    this.deactivatePlaceholder();
  }

  // ---------------- Placeholder button (variant_mapping mode) ----------------

  /**
   * Set up the placeholder button DOM. We insert a button-shaped div as a
   * sibling of the buy-buttons container, then toggle visibility of the
   * container (not individual buttons) to switch between "selection in
   * progress" (placeholder visible, buy-buttons hidden) and "selection
   * complete" (placeholder hidden, buy-buttons visible).
   *
   * Toggling the whole container rather than individual buttons leaves
   * Shopify's internal stock-state logic untouched — it decides which of
   * its buttons is visible WITHIN the container, and we just decide
   * whether to show the container at all.
   */
  setupPlaceholderButton() {
    const realBtn = this.getAddToCartButton();
    if (!realBtn) return;

    this._buyButtonsContainer = realBtn.parentElement;
    if (!this._buyButtonsContainer) return;

    // Inherit the real button's classes for matching brand styling
    const placeholder = document.createElement('div');
    placeholder.className =
      `${realBtn.className} truck-fitment-placeholder-btn`;
    placeholder.setAttribute('role', 'status');
    placeholder.setAttribute('aria-live', 'polite');
    placeholder.dataset.truckFitmentPlaceholder = '';
    placeholder.textContent = 'Select your truck to continue';

    // Insert AFTER the buy-buttons container so it sits in the same slot
    // when the container is hidden, and below it (or hidden) when active.
    this._buyButtonsContainer.parentElement.insertBefore(
      placeholder,
      this._buyButtonsContainer.nextSibling
    );
    this._placeholderBtn = placeholder;

    this.activatePlaceholder('Select your truck to continue');
  }

  /**
   * Show the placeholder and hide the buy-buttons container.
   */
  activatePlaceholder(message) {
    if (!this._placeholderBtn || !this._buyButtonsContainer) return;
    this._placeholderBtn.textContent = message;
    this._placeholderBtn.style.display = '';
    this._buyButtonsContainer.style.display = 'none';
    this._placeholderActive = true;
  }

  /**
   * Hide the placeholder and restore the buy-buttons container. Shopify's
   * own logic determines which button inside the container is visible
   * (Add to Cart vs. Sold Out / back-in-stock alert) based on stock.
   */
  deactivatePlaceholder() {
    if (!this._placeholderBtn || !this._buyButtonsContainer) return;
    this._placeholderBtn.style.display = 'none';
    this._buyButtonsContainer.style.display = '';
    this._placeholderActive = false;
  }

  // ---------------- Native variant picker ----------------

  hideNativeVariantPicker() {
    const selectors = [
      `#VariantPicker-${this.sectionId}-${this.productId}`,
      `#Variants-${this.sectionId}-${this.productId}`,
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) el.style.display = 'none';
    }
  }

  // ---------------- Reset / status / persistence ----------------

  resetDownstream(level) {
    if (level === 'make') {
      this.fillSelect(this.modelSelect, [], 'Select model');
      this.modelSelect.disabled = true;
    }
    this.hideRow(this.tallRow);
  }

  // ---------------- Progress track ----------------

  /**
   * Compute total expected steps based on current state, then update the
   * progress dots. Steps depend on:
   *   - Make: always counted
   *   - Model: always counted
   *   - Tall Edition: counted only if visible (selected truck has tall option)
   *   - Camper Model: counted only if 2+ visible camper models
   *   - Native variant: counted only in metadata_only mode
   */
  updateProgress() {
    if (!this.progressEl) return;

    const tallVisible = this.tallRow && !this.tallRow.hidden;
    const camperVisible = this.visibleCamperModels.length > 1;
    const needsNativeVariant = this.mode === 'metadata_only';

    const steps = [
      { name: 'make',   done: !!this.makeSelect.value },
      { name: 'model',  done: !!this.modelSelect.value },
    ];
    if (tallVisible) {
      steps.push({ name: 'tall', done: !!(this.tallSelect && this.tallSelect.value) });
    }
    if (camperVisible) {
      steps.push({ name: 'camper', done: !!(this.camperSelect && this.camperSelect.value) });
    }
    if (needsNativeVariant) {
      steps.push({ name: 'variant', done: this.nativeVariantSelected() });
    }

    // Rebuild dots if count changed
    if (this.progressEl.children.length !== steps.length) {
      this.progressEl.innerHTML = '';
      for (let i = 0; i < steps.length; i++) {
        const dot = document.createElement('span');
        dot.className = 'truck-fitment__progress-dot';
        this.progressEl.appendChild(dot);
      }
    }

    // Update done state
    for (let i = 0; i < steps.length; i++) {
      const dot = this.progressEl.children[i];
      if (!dot) continue;
      dot.classList.toggle('is-done', steps[i].done);
    }
  }

  /**
   * Update the user-facing status. Two layers of feedback:
   *   - The placeholder button (when selection isn't complete) shows the
   *     "what to do next" message via its own text.
   *   - The summary card shows confirmation/warning state ONLY when there's
   *     a meaningful resolved selection. We don't populate it for "Select
   *     your truck to continue" — that's the placeholder's job.
   *
   * type values:
   *   'ok'    — shows summary card with check icon, brand-orange accent
   *   'warn'  — shows summary card with warning icon, amber accent
   *             (incompatible camper, truck not listed)
   *   'error' — shows summary card with warning icon (rare; fitment load failures)
   *   'info'  — hides the summary card (used during in-progress selection)
   */
  setStatus(message, type = 'info') {
    if (!this.statusEl) return;
    this.statusEl.dataset.statusType = type;

    if (type === 'info') {
      this.clearStatusCard();
      return;
    }
    // For ok/warn/error, the message is a single string; we need to split
    // it into title and detail. We use the standard formats produced by
    // formatSuccessStatus and related helpers.
    this.renderStatusCardFromMessage(message, type);
  }

  /**
   * Set the summary card structurally — used by success-path callers that
   * already have title and detail separated.
   */
  setStatusCard({ title, detail, type = 'ok' }) {
    if (!this.statusEl) return;
    this.statusEl.dataset.statusType = type;
    this.statusEl.classList.toggle('truck-fitment__summary--warn', type === 'warn' || type === 'error');
    this.statusEl.hidden = false;
    if (this.summaryIcon) {
      this.summaryIcon.innerHTML = this.getSummaryIconSvg(type);
    }
    if (this.summaryTitle) this.summaryTitle.textContent = title || '';
    if (this.summaryDetail) {
      // Allow basic links in detail (e.g., contact us)
      this.summaryDetail.innerHTML = detail || '';
    }
  }

  clearStatusCard() {
    if (!this.statusEl) return;
    this.statusEl.hidden = true;
    this.statusEl.classList.remove('truck-fitment__summary--warn');
    if (this.summaryTitle) this.summaryTitle.textContent = '';
    if (this.summaryDetail) this.summaryDetail.innerHTML = '';
  }

  /**
   * Render summary card from a plain message string (for legacy callers that
   * pass a single string). Tries to extract a sensible title; falls back to
   * using the whole message as the title with no detail.
   */
  renderStatusCardFromMessage(message, type) {
    // Most non-success messages are single sentences. Show them as the title.
    this.setStatusCard({ title: message, detail: '', type });
  }

  getSummaryIconSvg(type) {
    if (type === 'warn' || type === 'error') {
      // Warning triangle
      return '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    }
    // Default: checkmark
    return '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
  }

  formatSuccessStatus(truckLabel, tallChoice, camper) {
    // Title: "Fits your <truck>" — drop the year prefix; the model string
    // already contains the year range
    const tallSuffix = tallChoice === true ? ' Tall Edition' : '';
    const title = `Fits your ${truckLabel}${tallSuffix}`;

    // Detail: camper only (heights and widths are intentionally hidden
    // from the customer — they're captured as line item properties for
    // fulfillment but aren't customer-facing concepts)
    let detail = '';
    if (camper && this.visibleCamperModels.length > 1) {
      detail = `${camper} camper`;
    }
    return { title, detail };
  }

  persist() {
    try {
      const data = {
        make: this.makeSelect.value,
        model: this.modelSelect.value,
        tall: this.tallSelect ? this.tallSelect.value : '',
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* noop */ }
  }

  clearStorage() {
    try { localStorage.removeItem(this.STORAGE_KEY); } catch (e) {}
  }

  restoreFromStorage() {
    let data = null;
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (raw) data = JSON.parse(raw);
    } catch (e) { return; }
    if (!data || !data.make) return;

    if (!this.fitment.some((r) => r.make === data.make)) return;
    this.makeSelect.value = data.make;
    this.populateModels(data.make);

    if (data.model) {
      const truck = this.findTruck(data.make, data.model);
      if (!truck) return;
      this.modelSelect.value = data.model;
      this.updateTallRowVisibility(truck);
      if (truck.height_tall && data.tall && this.tallSelect) {
        this.tallSelect.value = data.tall;
      }
    }

    this.recompute();
  }
}

if (!customElements.get('truck-fitment-selector')) {
  customElements.define('truck-fitment-selector', TruckFitmentSelector);
}
